import { FC } from 'react';
import { useEventGeneratorClickCta } from 'src/analytics/useEventGeneratorClickCta';
import { Button } from 'src/components';
import { useCtaHref } from 'src/hooks/useCtaHref';
import Card from 'src/features/PussInBoots/PussInBootsTopicOfTopicPageContent/LearnCommunicate/Card';
import Learnables from 'src/features/PussInBoots/PussInBootsTopicOfTopicPageContent/Learnables';
import LearnableCard from 'src/features/PussInBoots/PussInBootsTopicOfTopicPageContent/LearnableCard';
import { useIntl } from 'react-intl';
import { YoutubeScenario } from 'src/types/domain';
import { useTargetMessages } from 'src/hooks/useTargetMessages';
import * as S from '../styles';
import { Section, SectionContent } from './styles';
import * as msgsData from './messages';

const { msgs, ...localesWithMessages } = msgsData;

type LearnProps = { scenario: YoutubeScenario };

const PALETTE = 'blue';

const Learn: FC<LearnProps> = ({ scenario }) => {
  const intl = useIntl();
  const targetMsgs = useTargetMessages(localesWithMessages);
  const { learnables } = scenario;

  const signUpHref = useCtaHref();
  const eventGeneratorClickCta = useEventGeneratorClickCta();

  return (
    <Section>
      <SectionContent>
        <S.SubTitle>{intl.formatMessage(msgs.subtitle)}</S.SubTitle>
        <S.Title>{intl.formatMessage(targetMsgs.title)}</S.Title>
        <S.Description>{intl.formatMessage(targetMsgs.description)}</S.Description>

        <Card $palette={PALETTE}>
          <S.TopContainer>
            <S.LeftSideContainer>
              <S.LeftSideSubTitle>
                {intl.formatMessage(msgs.pibLearn)}
                <S.Icon name="starsSet" />
              </S.LeftSideSubTitle>
              <S.LeftSideTitle>
                {intl.formatMessage(targetMsgs.leftSectionTitle, {
                  amount: learnables.length,
                })}
              </S.LeftSideTitle>
              <S.LeftSideDescription>
                {intl.formatMessage(msgs.topSectionDescription)}
              </S.LeftSideDescription>
              <S.LeftSideLearnables>
                {learnables
                  .filter(l => l.videoUrls.length)
                  .slice(0, 3)
                  .map(learnable => (
                    <LearnableCard
                      $palette={PALETTE}
                      key={learnable.learnableId}
                      learnable={learnable}
                    />
                  ))}
              </S.LeftSideLearnables>
            </S.LeftSideContainer>

            <S.RightSideContainer>
              <S.RightSideLearnables>
                <Learnables learnables={learnables} $palette={PALETTE} />
              </S.RightSideLearnables>

              <S.RightSideCTA>
                <Button
                  analyticsEvent={eventGeneratorClickCta}
                  buttonSize="large"
                  buttonType="primary"
                  fullWidth="always"
                  href={signUpHref}
                >
                  {intl.formatMessage(msgs.vocabularyCta)}
                </Button>
              </S.RightSideCTA>
            </S.RightSideContainer>
          </S.TopContainer>

          <S.BottomContainer>
            <S.LeftSideContainer>
              <S.MissionCard mode="horizontal" mission="guessing-the-plot-of-the-movie" />
            </S.LeftSideContainer>
            <S.RightSideContainer>
              <S.LeftSideSubTitle>
                {intl.formatMessage(msgs.pibCommunicate)}
                <S.Icon name="starsSet" />
              </S.LeftSideSubTitle>
              <S.LeftSideTitle>{intl.formatMessage(targetMsgs.bottomSectionTitle)}</S.LeftSideTitle>
              <S.LeftSideDescription>
                {intl.formatMessage(msgs.bottomSectionDescription)}
              </S.LeftSideDescription>
            </S.RightSideContainer>
          </S.BottomContainer>
        </Card>

        <S.CTA>
          <Button
            analyticsEvent={eventGeneratorClickCta}
            buttonSize="large"
            buttonType="primary"
            disableMinWidth
            fullWidth="never"
            href={signUpHref}
          >
            {intl.formatMessage(msgs.learningCta)}
          </Button>
        </S.CTA>

        <S.AllRightReserved>{intl.formatMessage(msgs.pibAllRightReserved)}</S.AllRightReserved>
      </SectionContent>
    </Section>
  );
};

export default Learn;
